// Floating "Selected object" inspector card.
//
// Anchored in screen-space to the selected mesh: the host calls
// `setAnchor` from its render loop with the latest projected screen point,
// and we apply that to the card's `transform`. Avoids state-thrash by
// writing to a ref directly.
//
// Used by both Scene and Iso views. The card itself is dumb — kind/rotation/
// scale/accent/brand-mark/connect-to/delete handlers come in via props, so
// either view can wire its own selection model.

import React, { useImperativeHandle, useRef } from "react";
import type { BrandCanvasPose, BrandMaterialMode, BrandTextFont, RackRowPreset } from "./lib/types";

export type SelectionConnection = {
  /** Stable id of the link itself, used for individual disconnects. */
  id: string;
  /** Friendly label for the peer (e.g. its kind). */
  peerLabel: string;
};

export type SelectionCardProps = {
  kind: string;
  rotation: number;
  scale: number;
  accent: boolean;
  brandColor: BrandMaterialMode;
  brandMark: "icon" | "logo" | null;
  brandSide: "front" | "back" | "left" | "right" | "top" | null;
  /** Brand-mark thickness multiplier (1.0 = master default). Only meaningful
   *  when brandMark is non-null; when null/undefined treat as 1. */
  brandThickness: number;
  /** Rotate the placed brand mark 180 degrees within the chosen face. */
  brandFlip: boolean;
  brandInvert: boolean;
  isBrandCanvasObject: boolean;
  isTextObject: boolean;
  brandPose: BrandCanvasPose;
  textContent: string;
  textFont: BrandTextFont;
  textDepth: number;
  /** True when the selected object is the rack-row composite. */
  isRackRowObject: boolean;
  rowPreset: RackRowPreset;
  rowCount: number;
  rowPair: boolean;
  rowAisleWidth: number;
  /** True when the host is currently in "click another object to link" mode
   * with this object as the source. */
  linkingMode: boolean;
  /** Existing connections involving this object. */
  connections: SelectionConnection[];
  onChange: (patch: {
    rotation?: number;
    scale?: number;
    accent?: boolean;
    brandColor?: BrandMaterialMode;
    brandMark?: "icon" | "logo" | null;
    brandSide?: "front" | "back" | "left" | "right" | "top" | null;
    brandThickness?: number;
    brandFlip?: boolean;
    brandInvert?: boolean;
    brandPose?: BrandCanvasPose;
    textContent?: string;
    textFont?: BrandTextFont;
    textDepth?: number;
    rowPreset?: RackRowPreset;
    rowCount?: number;
    rowPair?: boolean;
    rowAisleWidth?: number;
  }) => void;
  /** Enter linking mode (host shows a hint and waits for a click on a peer). */
  onStartLink?: () => void;
  /** Cancel linking mode without creating a link. */
  onCancelLink?: () => void;
  /** Remove a single connection by its link id. */
  onDisconnect?: (linkId: string) => void;
  onClearLinks?: () => void;
  onDelete: () => void;
};

export type SelectionCardHandle = {
  /** Apply a screen-space anchor (CSS pixels relative to the canvas mount).
   * Pass null to hide the card briefly during repositioning churn. */
  setAnchor: (xy: { x: number; y: number } | null) => void;
};

export const SelectionCard = React.forwardRef<SelectionCardHandle, SelectionCardProps>(
  function SelectionCardInner(props, ref) {
    const {
      kind, rotation, scale, accent, brandColor, brandMark, brandSide, brandThickness, brandFlip, brandInvert,
      isBrandCanvasObject, isTextObject, brandPose, textContent, textFont, textDepth,
      isRackRowObject, rowPreset, rowCount, rowPair, rowAisleWidth,
      linkingMode, connections,
      onChange, onStartLink, onCancelLink, onDisconnect, onClearLinks, onDelete,
    } = props;
    const containerRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(ref, () => ({
      setAnchor(xy) {
        const el = containerRef.current;
        if (!el) return;
        if (!xy) {
          el.style.visibility = "hidden";
          return;
        }
        // Offset the card up-and-to-the-right of the anchor with enough gap
        // that it never sits on top of the object, then clamp to the parent
        // mount's viewport so it doesn't run off-screen near edges.
        const parent = el.offsetParent as HTMLElement | null;
        const pw = parent ? parent.clientWidth : window.innerWidth;
        const ph = parent ? parent.clientHeight : window.innerHeight;
        const cw = el.offsetWidth || 240;
        const ch = el.offsetHeight || 240;
        const margin = 12;
        // Default placement: well off to the right (140px) and well above
        // (160px) so the card is clearly out of the way and the object stays
        // unobscured. If that runs off-right, flip to the left side of the
        // anchor with the same generous gap.
        let dx = xy.x + 140;
        if (dx + cw > pw - margin) dx = xy.x - cw - 140;
        let dy = xy.y - 160;
        // Clamp vertically.
        if (dy < margin) dy = margin;
        if (dy + ch > ph - margin) dy = ph - ch - margin;
        if (dx < margin) dx = margin;
        if (dx + cw > pw - margin) dx = pw - cw - margin;
        el.style.visibility = "visible";
        el.style.transform = `translate(${Math.round(dx)}px, ${Math.round(dy)}px)`;
      },
    }), []);

    // While the user is mid-connect, dim the card and disable its
    // interactions so it doesn't get in the way of clicking the target.
    // It still shows where the source object is (low opacity) so the user
    // remembers where the link will originate. Escape (handled in the
    // host's onKey) cancels and brings it back.
    const cardStyle: React.CSSProperties = linkingMode
      ? { ...styles.card, opacity: 0.18, pointerEvents: "none", filter: "saturate(0.5)" }
      : styles.card;
    return (
      <div ref={containerRef} style={cardStyle}>
        <div style={styles.header}>
          <span style={styles.kindLabel}>{kind}</span>
          <button style={styles.closeBtn} onClick={onDelete} title="Delete">×</button>
        </div>
        <SliderRow
          label="Rotation"
          min={-Math.PI} max={Math.PI} step={Math.PI / 12}
          value={rotation}
          onChange={(v) => onChange({ rotation: v })}
          fmt={(v) => `${Math.round((v * 180) / Math.PI)}°`}
        />
        <SliderRow
          label="Scale"
          min={0.3} max={3.0} step={0.05}
          value={scale}
          onChange={(v) => onChange({ scale: v })}
        />
        {!isBrandCanvasObject && !isRackRowObject && (
          <label style={styles.checkRow}>
            <input
              type="checkbox"
              checked={accent}
              onChange={(e) => onChange({ accent: e.target.checked })}
            />
            Accent (brand blue)
          </label>
        )}

        {isRackRowObject && (
          <div style={styles.fieldGroup}>
            <div style={styles.fieldLabel}>Rack row</div>
            <label style={styles.controlRow}>
              Preset
              <select
                value={rowPreset}
                onChange={(e) => onChange({ rowPreset: e.target.value as RackRowPreset })}
                style={styles.select}
              >
                <option value="single">Single (1)</option>
                <option value="short">Short (4)</option>
                <option value="medium">Medium (8)</option>
                <option value="long">Long (16)</option>
                <option value="hall">Hall (32)</option>
                <option value="custom">Custom…</option>
              </select>
            </label>
            {rowPreset === "custom" && (
              <SliderRow
                label="Cabinets"
                min={1} max={64} step={1}
                value={rowCount}
                onChange={(v) => onChange({ rowCount: Math.round(v) })}
                fmt={(v) => `${Math.round(v)}`}
              />
            )}
            <label style={styles.checkRow}>
              <input
                type="checkbox"
                checked={rowPair}
                onChange={(e) => onChange({ rowPair: e.target.checked })}
              />
              Mirror across aisle
            </label>
            {rowPair && (
              <SliderRow
                label="Aisle"
                min={1.2} max={6.0} step={0.2}
                value={rowAisleWidth}
                onChange={(v) => onChange({ rowAisleWidth: v })}
                fmt={(v) => `${v.toFixed(1)} m`}
              />
            )}
          </div>
        )}

        {isBrandCanvasObject && (
          <div style={styles.fieldGroup}>
            <div style={styles.fieldLabel}>{isTextObject ? "Text" : "Brand object"}</div>
            <MaterialSelect
              label="Material"
              value={brandColor}
              onChange={(value) => onChange({ brandColor: value, accent: value === "accent" })}
            />
            {isTextObject && (
              <>
                <input
                  type="text"
                  value={textContent}
                  onChange={(e) => onChange({ textContent: e.target.value })}
                  style={styles.textInput}
                />
                <label style={styles.controlRow}>
                  Font
                  <select
                    value={textFont}
                    onChange={(e) => onChange({ textFont: e.target.value as BrandTextFont })}
                    style={styles.select}
                  >
                    <option value="sans">Saans</option>
                    <option value="mono">Fira Code</option>
                  </select>
                </label>
                <SliderRow
                  label="Depth"
                  min={0.02} max={0.8} step={0.02}
                  value={textDepth}
                  onChange={(v) => onChange({ textDepth: v })}
                />
              </>
            )}
            {!isTextObject && (
              <SliderRow
                label="Thickness"
                min={0.2} max={3} step={0.05}
                value={brandThickness}
                onChange={(v) => onChange({ brandThickness: v })}
                fmt={(v) => `${v.toFixed(2)}×`}
              />
            )}
            <label style={styles.controlRow}>
              Orientation
              <select
                value={brandPose}
                onChange={(e) => onChange({ brandPose: e.target.value as BrandCanvasPose })}
                style={styles.select}
              >
                <option value="upright">Upright</option>
                <option value="flat">Flat</option>
              </select>
            </label>
            <label style={styles.checkRow}>
              <input
                type="checkbox"
                checked={brandFlip}
                onChange={(e) => onChange({ brandFlip: e.target.checked })}
              />
              Flip 180°
            </label>
          </div>
        )}

        {!isBrandCanvasObject && !isRackRowObject && (
          <div style={styles.fieldGroup}>
          <div style={styles.fieldLabel}>Brand mark</div>
          <div style={{ display: "flex", gap: 8 }}>
            <RadioInline name="brand" label="None" checked={brandMark === null}
              onChange={() => onChange({ brandMark: null, brandSide: null, brandFlip: false })} />
            <RadioInline name="brand" label="Icon" checked={brandMark === "icon"}
              onChange={() => onChange({ brandMark: "icon", brandSide: brandSide ?? "top" })} />
            <RadioInline name="brand" label="Logo" checked={brandMark === "logo"}
              onChange={() => onChange({ brandMark: "logo", brandSide: brandSide ?? "top" })} />
          </div>
          <label style={{
            display: "flex", alignItems: "center", gap: 6, fontSize: 11, marginTop: 2,
            opacity: brandMark ? 1 : 0.45,
          }}>
            Side
            <select
              value={brandSide ?? "top"}
              disabled={!brandMark}
              onChange={(e) => onChange({
                brandSide: e.target.value as "front" | "back" | "left" | "right" | "top",
              })}
              style={{ flex: 1, fontSize: 11 }}
            >
              <option value="front">Front</option>
              <option value="back">Back</option>
              <option value="left">Left</option>
              <option value="right">Right</option>
              <option value="top">Top</option>
            </select>
          </label>
          <MaterialSelect
            label="Colour"
            value={brandColor}
            disabled={!brandMark}
            onChange={(value) => onChange({ brandColor: value })}
          />
          <label style={{
            display: "flex", alignItems: "center", gap: 6, fontSize: 11, marginTop: 2,
            opacity: brandMark ? 1 : 0.45,
          }}>
            <input
              type="checkbox"
              checked={brandFlip}
              disabled={!brandMark}
              onChange={(e) => onChange({ brandFlip: e.target.checked })}
            />
            Flip 180°
          </label>
          {/* Invert (horizontal mirror). Different from Flip — useful when a
              side's basis ends up showing the icon mirrored (e.g. the "right"
              face has its X axis opposite to "left", so the same authored
              glyph reads reversed). One-click correction. */}
          <label style={{
            display: "flex", alignItems: "center", gap: 6, fontSize: 11, marginTop: 2,
            opacity: brandMark ? 1 : 0.45,
          }}>
            <input
              type="checkbox"
              checked={brandInvert}
              disabled={!brandMark}
              onChange={(e) => onChange({ brandInvert: e.target.checked })}
            />
            Invert
          </label>
          {/* Thickness slider — multiplier on the master's authored 0.32m
              extrusion. Disabled when no brand mark is selected. */}
          <label style={{
            display: "flex", flexDirection: "column", gap: 2, marginTop: 2,
            opacity: brandMark ? 1 : 0.45,
          }}>
            <span style={{ display: "flex", justifyContent: "space-between", color: "var(--muted)", fontSize: 11 }}>
              Thickness
              <span style={{ color: "var(--fg)" }}>{brandThickness.toFixed(2)}×</span>
            </span>
            <input
              type="range"
              min={0.2} max={3} step={0.05}
              value={brandThickness}
              disabled={!brandMark}
              onChange={(e) => onChange({ brandThickness: Number(e.target.value) })}
              style={{ width: "100%" }}
            />
          </label>
          </div>
        )}

        {(onStartLink || onClearLinks || (connections && connections.length > 0)) && (
          <div style={styles.fieldGroup}>
            <div style={styles.fieldLabel}>Connections</div>
            {connections && connections.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {connections.map((c) => (
                  <div key={c.id} style={styles.connectionRow}>
                    <span style={{ color: "var(--fg)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      → {c.peerLabel}
                    </span>
                    {onDisconnect && (
                      <button
                        style={styles.miniBtn}
                        onClick={() => onDisconnect(c.id)}
                        title="Disconnect"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {linkingMode ? (
              <div style={styles.linkingPrompt}>
                <span style={{ color: "var(--accent)" }}>Click target object…</span>
                {onCancelLink && (
                  <button style={styles.miniBtn} onClick={onCancelLink} title="Cancel">×</button>
                )}
              </div>
            ) : (
              <div style={{ display: "flex", gap: 4 }}>
                {onStartLink && (
                  <button style={styles.btn} onClick={onStartLink} title="Click an object on this level to connect">
                    + Connect to…
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

function RadioInline(props: { name: string; label: string; checked: boolean; onChange: () => void }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
      <input type="radio" name={props.name} checked={props.checked} onChange={props.onChange} />
      {props.label}
    </label>
  );
}

function MaterialSelect(props: {
  label: string;
  value: BrandMaterialMode;
  disabled?: boolean;
  onChange: (value: BrandMaterialMode) => void;
}) {
  const { label, value, disabled = false, onChange } = props;
  return (
    <label style={{
      ...styles.controlRow,
      opacity: disabled ? 0.45 : 1,
    }}>
      {label}
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as BrandMaterialMode)}
        style={styles.select}
      >
        <option value="white">White</option>
        <option value="neutral">Neutral</option>
        <option value="accent">Accent</option>
      </select>
    </label>
  );
}

function SliderRow({
  label, min, max, step, value, onChange, fmt,
}: {
  label: string; min: number; max: number; step: number; value: number;
  onChange: (v: number) => void; fmt?: (v: number) => string;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ display: "flex", justifyContent: "space-between", color: "var(--muted)", fontSize: 11 }}>
        {label}<span style={{ color: "var(--fg)" }}>{fmt ? fmt(value) : value.toFixed(2)}</span>
      </span>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: "100%" }}
      />
    </label>
  );
}

const PANEL_BG = "rgba(15, 15, 18, 0.92)";
const PANEL_BORDER = "1px solid #1f1f24";

const styles: Record<string, React.CSSProperties> = {
  card: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 240,
    background: PANEL_BG,
    border: PANEL_BORDER,
    borderRadius: 6,
    padding: 10,
    fontSize: 12,
    color: "var(--fg)",
    boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    pointerEvents: "auto",
    zIndex: 5,
    backdropFilter: "blur(6px)",
    visibility: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    paddingBottom: 4,
    borderBottom: "1px solid var(--line)",
  },
  kindLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    color: "var(--muted)",
  },
  closeBtn: {
    background: "transparent",
    color: "var(--muted)",
    border: "none",
    cursor: "pointer",
    fontSize: 16,
    padding: "0 4px",
    lineHeight: 1,
  },
  checkRow: { display: "flex", alignItems: "center", gap: 6, fontSize: 11 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 4 },
  fieldLabel: { color: "var(--muted)", fontSize: 11 },
  controlRow: { display: "flex", alignItems: "center", gap: 6, fontSize: 11 },
  select: {
    flex: 1,
    background: "var(--bg)",
    color: "var(--fg)",
    border: PANEL_BORDER,
    borderRadius: 3,
    padding: "3px 5px",
    fontSize: 11,
  },
  textInput: {
    width: "100%",
    background: "var(--bg)",
    color: "var(--fg)",
    border: PANEL_BORDER,
    borderRadius: 3,
    padding: "4px 6px",
    fontSize: 11,
  },
  btn: {
    background: "var(--bg)",
    color: "var(--fg)",
    border: PANEL_BORDER,
    padding: "4px 8px",
    borderRadius: 3,
    cursor: "pointer",
    fontSize: 11,
  },
  miniBtn: {
    background: "transparent",
    color: "var(--muted)",
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    padding: "0 4px",
    lineHeight: 1,
  },
  connectionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "var(--bg)",
    border: PANEL_BORDER,
    borderRadius: 3,
    padding: "3px 6px",
    fontSize: 11,
    gap: 6,
  },
  linkingPrompt: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "var(--bg)",
    border: "1px dashed var(--accent)",
    borderRadius: 3,
    padding: "4px 6px",
    fontSize: 11,
  },
};
